class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = null;
    this.allPredictiveSearchInstances = [];
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this._suggestSeq = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    /* Faster refresh than SearchForm's 300ms; replace parent's debounced handler on this input */
    if (this.input && this._debouncedChange) {
      if (this._predictiveInputSchedule) {
        this.input.removeEventListener('input', this._predictiveInputSchedule);
      }
      this.input.removeEventListener('compositionend', this._debouncedChange);
    }
    if (this.input) {
      this._debouncedChange = debounce(() => {
        this.onChange();
      }, 120).bind(this);
      this._predictiveInputSchedule = () => {
        if (this.input.isComposing) return;
        this._debouncedChange();
      };
      this.input.addEventListener('input', this._predictiveInputSchedule);
      /* compositionend: value is final; input events may be skipped while isComposing */
      this.input.addEventListener('compositionend', this._debouncedChange);
    }

    if (this._predictiveSearchInitialized) return;

    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    if (!this.input || !this.predictiveSearchResults) return;

    this._predictiveSearchInitialized = true;
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input?.form) return;

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.syncSearchForLabel(this.searchTerm);
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.syncSearchForLabel(this.searchTerm);
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    /* Mobile Hebrew keyboards: don't intercept IME composition (Enter/arrows commit text) */
    if (event.isComposing || event.keyCode === 229) return;

    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        if (this.getAttribute('open')) {
          event.preventDefault();
          this.switchOption('up');
        }
        break;
      case 'ArrowDown':
        if (this.getAttribute('open')) {
          event.preventDefault();
          this.switchOption('down');
        }
        break;
      case 'Enter': {
        const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
        if (selected) {
          event.preventDefault();
          this.selectOption();
        }
        break;
      }
    }
  }

  onKeydown(event) {
    if (!this.getAttribute('open')) return;
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  /**
   * Keep "search for …" in sync with the input using a Liquid-provided template (avoids fragile
   * string replace when the query repeats letters that also appear in the translated prefix).
   */
  syncSearchForLabel(term) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    if (!el) return;
    let tpl = this.getAttribute('data-search-for-template');
    const sentinel = '[[search_terms]]';
    if (!tpl || !String(term || '').trim()) return;
    /* Liquid | escape can leave literal &quot; in the attribute string; normalize for textContent */
    if (tpl.includes('&')) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = tpl;
      tpl = decoder.value;
    }
    if (!tpl.includes(sentinel)) return;
    el.textContent = tpl.split(sentinel).join(term);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const trimmed = String(searchTerm || '').trim();
    if (!trimmed) {
      this.close(true);
      return;
    }

    const queryKey = trimmed.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this._suggestSeq = (this._suggestSeq || 0) + 1;
    const reqId = this._suggestSeq;

    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    const defaultSuggestHref = routes.predictive_search_url;
    const originBase = window.location.origin;

    const localizedBase =
      routes.predictive_language_suggest_base && String(routes.predictive_language_suggest_base).trim();
    /* When Markets use a language subpath, suggest must hit that path — not bare /search/suggest (417). */
    const hePrefixedSuggestPath = '/he/search/suggest';

    const suggestHrefFromRootPath = (rootPath) => {
      const r = String(rootPath || '')
        .trim()
        .replace(/\/+$/, '');
      if (!r || r === '/') return '';
      const base = r.startsWith('/') ? r : `/${r}`;
      return `${base}/search/suggest`.replace(/\/{2,}/g, '/');
    };

    const documentIsRtl = () => {
      const h = (document.documentElement.getAttribute('dir') || '').toLowerCase();
      if (h === 'rtl') return true;
      const b = document.body && (document.body.getAttribute('dir') || '').toLowerCase();
      return b === 'rtl';
    };

    const primarySuggestHref = (() => {
      if (localizedBase && localizedBase !== '/') {
        const norm = localizedBase.replace(/\/$/, '');
        if (!norm.includes('://')) {
          return `${norm.startsWith('/') ? '' : '/'}${norm}/search/suggest`.replace(/\/{2,}/g, '/');
        }
      }
      /* Shopify localized root (e.g. /he) — often correct when iso_code is still "en" for default */
      const fromRoutesRoot = suggestHrefFromRootPath(routes.predictive_root_url ?? '');
      if (fromRoutesRoot) return fromRoutesRoot;

      /* Store locale from Liquid; html lang can be wrong and cause bare /search/suggest → 417 */
      const storeIso = String(routes.predictive_locale_iso || '').toLowerCase();
      if (storeIso === 'he' || storeIso === 'iw') {
        return hePrefixedSuggestPath;
      }
      const pathM = window.location.pathname.match(/^\/(he|iw)(\/|$)/i);
      if (pathM) {
        return hePrefixedSuggestPath;
      }
      const lang = (document.documentElement.lang || '').toLowerCase();
      if (lang === 'he' || lang === 'iw' || lang.startsWith('he-') || lang.startsWith('iw-')) {
        return hePrefixedSuggestPath;
      }
      if (documentIsRtl()) {
        return hePrefixedSuggestPath;
      }
      if (routes.predictive_force_he_suggest === true) {
        return hePrefixedSuggestPath;
      }
      return defaultSuggestHref;
    })();

    /* If first URL still 417, retry once (e.g. rare path mismatch). */
    const suggestAcceptLanguage = (() => {
      const fromTheme = routes.predictive_accept_language && String(routes.predictive_accept_language).trim();
      if (fromTheme) return fromTheme;
      const h = (document.documentElement.lang || '').trim();
      if (!h) return 'en-US,en;q=0.8';
      const low = h.toLowerCase();
      if (low === 'he' || low === 'iw' || low.startsWith('he-') || low.startsWith('iw-')) {
        return 'he-IL,he,en;q=0.5,en-US;q=0.3';
      }
      return `${h},en;q=0.5,en-US;q=0.3`;
    })();

    const buildSuggestUrl = (href) => {
      const raw = String(href || '').trim();
      const u = raw.startsWith('http') ? new URL(raw) : new URL(raw.startsWith('/') ? raw : `/${raw}`, originBase);
      /* Dawn-style literal q= — avoids rare URLSearchParams + Unicode issues; keeps path as-is (/he/search/suggest). */
      const base = `${u.origin}${u.pathname}`;
      const q = encodeURIComponent(trimmed);
      return `${base}?q=${q}&section_id=predictive-search&_pscb=${Date.now()}`;
    };

    const suggestEnBuyerLang = 'en-US,en;q=0.9';

    const fetchHeadersFor = (acceptLang) => ({
      'Cache-Control': 'no-cache',
      Accept: 'text/html',
      'Accept-Language': acceptLang,
      'X-Requested-With': 'XMLHttpRequest',
    });

    /** Liquid {% else %} when performed is false includes type="button" on the term row; true branch does not. */
    const suggestMarkupIsNotPerformedShell = (html) => {
      if (!html || !String(html).includes('predictive-search-option-search-keywords')) return false;
      const s = String(html);
      return s.includes('type="button"') && !s.includes('id="predictive-search-results-groups-wrapper"');
    };

    const fetchSuggestRaw = (href, acceptLang) =>
      fetch(buildSuggestUrl(href), {
        signal: this.abortController.signal,
        cache: 'no-store',
        credentials: 'include',
        headers: fetchHeadersFor(acceptLang),
      }).then((response) => {
        const st = Number(response.status);
        if (st === 417) {
          try {
            const u = new URL(buildSuggestUrl(href));
            if (u.pathname === '/search/suggest') {
              return fetch(buildSuggestUrl(hePrefixedSuggestPath), {
                signal: this.abortController.signal,
                cache: 'no-store',
                credentials: 'include',
                headers: fetchHeadersFor(acceptLang),
              }).then((r2) => {
                if (!r2.ok) {
                  return Promise.reject(new Error(`Predictive search HTTP ${r2.status}`));
                }
                return r2.text();
              });
            }
          } catch (_) {
            /* fall through */
          }
        }
        if (!response.ok) {
          return Promise.reject(new Error(`Predictive search HTTP ${response.status}`));
        }
        return response.text();
      });

    const fetchSuggestText = (href) => fetchSuggestRaw(href, suggestAcceptLanguage);

    const fallbackSectionId = routes.predictive_search_fallback_section_id || 'search-dropdown';

    const fetchSearchFallbackSectionHtml = () => {
      const rawSearch = (routes.search_url || '/search').trim();
      const su = rawSearch.startsWith('http')
        ? new URL(rawSearch)
        : new URL(rawSearch.startsWith('/') ? rawSearch : `/${rawSearch}`, originBase);
      const qEnc = encodeURIComponent(trimmed);
      const url = `${su.origin}${su.pathname}?q=${qEnc}&type=product&options%5Bprefix%5D=last&section_id=${encodeURIComponent(
        fallbackSectionId
      )}&_psfb=${Date.now()}`;
      return fetch(url, {
        signal: this.abortController.signal,
        cache: 'no-store',
        credentials: 'include',
        headers: fetchHeadersFor(suggestAcceptLanguage),
      }).then((response) => {
        if (!response.ok) {
          return Promise.reject(new Error(`Search dropdown section HTTP ${response.status}`));
        }
        return response.text();
      });
    };

    fetchSuggestText(primarySuggestHref)
      .then((text) => {
        if (reqId !== this._suggestSeq) return null;
        if (!text || !String(text).trim()) {
          this.close();
          return null;
        }
        if (suggestMarkupIsNotPerformedShell(text)) {
          /* Bare /search/suggest often 417 here — use Hebrew path + en buyer Accept-Language */
          return fetchSuggestRaw(hePrefixedSuggestPath, suggestEnBuyerLang)
            .then((t2) => {
              if (reqId !== this._suggestSeq) return null;
              return suggestMarkupIsNotPerformedShell(t2) ? text : t2;
            })
            .catch(() => (reqId !== this._suggestSeq ? null : text));
        }
        return text;
      })
      .then((text) => {
        if (reqId !== this._suggestSeq) return null;
        if (text == null || !String(text).trim()) return null;
        /* Shopify predictive_search is not populated for unsupported buyer locales (e.g. Hebrew). */
        if (suggestMarkupIsNotPerformedShell(text)) {
          return fetchSearchFallbackSectionHtml().then((fb) => {
            if (reqId !== this._suggestSeq) return null;
            return fb && String(fb).trim() ? fb : text;
          });
        }
        return text;
      })
      .then((text) => {
        if (reqId !== this._suggestSeq) return;
        if (text == null || !String(text).trim()) {
          return;
        }
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section =
          doc.querySelector('#shopify-section-predictive-search') ||
          doc.querySelector('.shopify-section[id*="predictive-search"]') ||
          doc.querySelector(`#shopify-section-${fallbackSectionId}`) ||
          doc.querySelector('.shopify-section');
        if (!section || !section.innerHTML.trim()) {
          this.close();
          return;
        }
        const markup = section.innerHTML;
        if (!suggestMarkupIsNotPerformedShell(text)) {
          this.allPredictiveSearchInstances.forEach((inst) => {
            inst.cachedResults[queryKey] = markup;
          });
        }
        this.renderSearchResults(markup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.syncSearchForLabel(this.getQuery().trim() || this.searchTerm);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const live = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (live?.textContent) {
      this.setLiveRegionText(live.textContent);
    }
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    const bottom = header?.getBoundingClientRect().bottom;
    const space =
      typeof bottom === 'number' && !Number.isNaN(bottom) && bottom > 0
        ? window.innerHeight - bottom
        : Math.min(window.innerHeight * 0.55, 480);
    this.resultsMaxHeight = Math.max(120, space);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    const maxH = this.resultsMaxHeight || this.getResultsMaxHeight();
    this.predictiveSearchResults.style.maxHeight =
      typeof maxH === 'number' && !Number.isNaN(maxH) ? `${maxH}px` : 'min(55vh, 480px)';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (!this.input || !this.predictiveSearchResults) return;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
